import { pxArrayToRem, pxToRem, type StyleRule } from '@archipro-design/aria';
import EditorConfig from './editor-config';

const NON_SCALING_CONTENT_FONTSIZE = '16px';

export const RichTextEditorNodeStyling: StyleRule<{
    /**
     * Hand the type over to the revamp's own definition.
     *
     * The article revamp sets its prose in Tailwind (`REVAMP_BODY_PROSE`) on a
     * wrapper around this element, and a descendant selector there ties on
     * specificity with the rules below — source order would decide which of
     * the two a paragraph got. So under the revamp this rule keeps only what
     * the revamp does not state: the inline marks, the link colour and the
     * list resets Tailwind's preflight removed. Everything the revamp sets —
     * size, leading, colour, heading and quote treatment, paragraph rhythm —
     * is left to it, and there is nothing to out-rank.
     */
    revamp?: boolean;
}> = ({ theme, revamp }) => {
    const bodyInk =
        theme.siteVariables.bodyColor ||
        theme.siteVariables.colors.charcoal['250'];
    const linkStyles = {
        // Revamp prose owns the ink; links follow it instead of pinned charcoal.
        color: revamp ? 'currentColor' : bodyInk,
        textDecoration: 'underline',
    };
    return {
        ...(revamp
            ? {}
            : {
                  color: bodyInk,
                  fontSize: pxToRem(16),
                  lineHeight: theme.siteVariables.lineHeightSmall,

                  [theme.screen.laptop]: {
                      fontSize: NON_SCALING_CONTENT_FONTSIZE,
                  },
              }),

        [`& .${EditorConfig.theme.text.bold}`]: {
            // Match Lexical bold to public-page <strong> weight.
            fontWeight: theme.siteVariables.fontWeightBold,
        },
        [`& .${EditorConfig.theme.text.italic}`]: {
            fontStyle: 'italic',
        },
        [`& .${EditorConfig.theme.text.underline}`]: {
            textDecoration: 'underline',
        },
        [`& .${EditorConfig.theme.link}`]: {
            ...linkStyles,
            '&span': linkStyles,
            '&strong': linkStyles,
            '&i': linkStyles,
            '&em': linkStyles,
        },
        [`& a.${EditorConfig.theme.link}:link`]: {
            ...linkStyles,
        },
        ...(revamp
            ? {}
            : {
                  [`& .${EditorConfig.theme.heading.h2}`]: {
                      textAlign: 'center',
                      fontSize: pxToRem(24),
                      lineHeight: theme.siteVariables.lineHeightSmallest,
                      letterSpacing: theme.siteVariables.letterSpacingMedium,
                      ...theme.siteVariables.getTextStyle(
                          'Medium65',
                          'Article',
                      ),
                      [theme.screen.laptop]: {
                          fontSize: pxToRem(30),
                          lineHeight: theme.siteVariables.lineHeightSmaller,
                          letterSpacing: theme.siteVariables.letterSpacingSmall,
                      },
                  },
                  [`& .${EditorConfig.theme.heading.h3}, & .${EditorConfig.theme.heading.h4}`]:
                      {
                          marginBottom: theme.siteVariables.spaces['space-28'],
                          fontSize: pxToRem(20),
                          lineHeight: 1.6,
                          ...theme.siteVariables.textStyles.Text.Medium65,
                          [theme.screen.laptop]: {
                              fontSize: pxToRem(24),
                          },
                      },
                  [`& .${EditorConfig.theme.quote}`]: {
                      margin: pxArrayToRem([30, 0]),
                      padding: pxArrayToRem([40, 0]),
                      fontSize: theme.siteVariables.fontSizes.label01,
                      lineHeight: theme.siteVariables.lineHeightSmallest,
                      letterSpacing: theme.siteVariables.letterSpacingMedium,
                      borderTop: `1px solid ${theme.siteVariables.bodyColor}`,
                      borderBottom: `1px solid ${theme.siteVariables.bodyColor}`,
                      width: '100%',

                      [theme.screen.laptop]: {
                          margin: pxArrayToRem([40, 0]),
                          fontSize:
                              theme.siteVariables.fontSizes['subheading03-alt'],
                          letterSpacing: theme.siteVariables.letterSpacingSmall,
                          lineHeight: theme.siteVariables.lineHeightSmaller,
                      },

                      '&:has(span)::before': {
                          content: 'open-quote',
                      },
                      '&:has(span)::after': {
                          content: 'close-quote',
                      },
                  },
                  '& p': {
                      marginBottom: pxToRem(16),
                  },
              }),
        // Restore markers Tailwind preflight strips. The public revamp page
        // does the same via REVAMP_BODY_PROSE; these keep the Lexical canvas aligned.
        '& ul, & menu': {
            listStyle: 'disc',
            paddingLeft: '40px',
            marginBottom: pxToRem(16),
        },
        '& ol': {
            listStyle: 'decimal',
            paddingLeft: '40px',
            marginBottom: pxToRem(16),
        },
        '& > *:last-child': {
            marginBottom: 0,
        },
    };
};
